// The real bid book: funded offers from the offers API shaped for the /bids
// board and the activity rails. Runs on the server for the first paint and in
// the browser for polling, so it takes its fetch options from the caller.

import { API_URL } from '@/utils/constants'
import { fungibleOffersEnabled, offerFundingReplaced, offerTargetIsFungible, type Offer } from '@/utils/offers'
import type {
	BidActivity,
	BidCollection,
	CollectionBid,
	InscriptionBid,
	InscriptionBidGroup
} from '@/utils/bids'

export interface RealBidBook {
	collectionBids: CollectionBid[]
	inscriptionBids: InscriptionBid[]
	activity: BidActivity[]
}

export const EMPTY_BOOK: RealBidBook = { collectionBids: [], inscriptionBids: [], activity: [] }

export type CollectionLookup = (slug: string) => Promise<BidCollection | null>

/** Collection metadata for slugs the trending list did not carry; cached per process or tab. */
const collectionCache = new Map<string, Promise<BidCollection | null>>()

export function collectionLookup(known: Map<string, BidCollection>, init: RequestInit): CollectionLookup {
	return (slug) => {
		const hit = known.get(slug)
		if (hit) return Promise.resolve(hit)
		let pending = collectionCache.get(slug)
		if (!pending) {
			pending = fetch(`${API_URL}/collection/${encodeURIComponent(slug)}`, init)
				.then((r) => (r.ok ? r.json() : null))
				.then((c: any) =>
					c?.slug
						? {
								slug: c.slug,
								name: c.name || c.slug,
								icon: c.icon ?? null,
								icon_inscription: c.icon_inscription ?? null,
								verified: !!c.verified,
								floor_price: c.floor_price != null ? Number(c.floor_price) : null,
								total_supply: c.total_supply ?? null,
								listed: c.listed ?? null
						  }
						: {
								slug,
								name: slug,
								icon: null,
								icon_inscription: null,
								verified: false,
								floor_price: null,
								total_supply: null,
								listed: null
						  }
				)
				.catch(() => {
					// A failed lookup must not poison every later poll.
					collectionCache.delete(slug)
					return null
				})
			collectionCache.set(slug, pending)
		}
		return pending
	}
}

/** Funded offers, shaped for the board and the stream. */
export async function toRealBook(offers: Offer[], lookup: CollectionLookup): Promise<RealBidBook> {
	const now = Date.now()
	// Bids are NFT-only: token collections stay out of the book and the stream.
	offers = offers.filter(
		(o) => fungibleOffersEnabled() || !offerTargetIsFungible(o.collection_slug, { collection_slugs: [] })
	)
	const slugs = Array.from(new Set(offers.map((o) => o.collection_slug).filter((s): s is string => !!s)))
	const metas = await Promise.all(slugs.map((slug) => lookup(slug)))
	const bySlug = new Map(slugs.map((slug, i) => [slug, metas[i]]))
	const collectionBids: CollectionBid[] = []
	const inscriptionBids: InscriptionBid[] = []
	const activity: BidActivity[] = []
	for (const o of offers) {
		const slug = o.collection_slug
		const collection = slug ? bySlug.get(slug) : null
		if (!slug || !collection) continue
		const live = o.state === 'active' && Date.parse(o.expires_at) > now
		const traitCollection = o.trait_value ? { ...collection, name: `${collection.name} · ${o.trait_value}` } : collection
		const itemId = o.filled_inscription_id || o.inscription_id
		if (live && o.scope !== 'item')
			collectionBids.push({
				id: `offer-${o.id}`,
				slug,
				collection: traitCollection,
				priceSats: o.price_sats,
				floorSats: collection.floor_price ?? null,
				quantity: 1,
				bidder: o.buyer_address,
				createdAt: Date.parse(o.created_at)
			})
		if (live && o.scope === 'item' && itemId)
			inscriptionBids.push({
				id: `offer-${o.id}`,
				slug,
				collection,
				inscriptionId: itemId,
				name: collection.name,
				priceSats: o.price_sats,
				listedSats: null,
				bidder: o.buyer_address,
				createdAt: Date.parse(o.created_at)
			})
		const base = {
			slug,
			// The compact activity rail is intentionally collection-level. Trait
			// values such as boolean metadata and internal enum names are not
			// useful activity labels; richer eligibility copy belongs in the
			// offer detail UI.
			collection,
			priceSats: o.price_sats,
			bidder: o.buyer_address,
			scope: (o.scope === 'item' ? 'inscription' : 'collection') as 'inscription' | 'collection',
			inscriptionId: itemId || undefined,
			name: collection.name
		}
		activity.push({ ...base, id: `real-placed-${o.id}`, kind: 'placed', at: Date.parse(o.created_at) })
		// updated_at moves whenever the reconciler touches a settled offer; the
		// fill belongs where the settlement happened.
		if (o.state === 'accepted' || o.state === 'settling')
			activity.push({
				...base,
				id: `real-filled-${o.id}`,
				kind: 'filled',
				at: Date.parse(o.accepted_at || o.updated_at)
			})
		// A funding replaced or evicted before it confirmed ends the offer like a
		// cancel (the API voids it as `cancelled` with no refund transaction).
		if (o.state === 'cancelled' || o.state === 'cancelling')
			activity.push({
				...base,
				id: `real-cancelled-${o.id}`,
				kind: 'cancelled',
				...(offerFundingReplaced(o) ? { label: 'Funding replaced' } : {}),
				at: Date.parse(o.updated_at)
			})

	}
	collectionBids.sort((a, b) => b.priceSats - a.priceSats)
	activity.sort((a, b) => b.at - a.at)
	return { collectionBids, inscriptionBids, activity }
}

/** Inscription bids grouped by collection, deepest books first. */
export function groupInscriptionBids(bids: InscriptionBid[]): InscriptionBidGroup[] {
	const bySlug = new Map<string, InscriptionBidGroup>()
	for (const bid of bids) {
		const g = bySlug.get(bid.slug) ?? { slug: bid.slug, collection: bid.collection, totalSats: 0, bids: [] }
		g.bids.push(bid)
		bySlug.set(bid.slug, g)
	}
	return [...bySlug.values()]
		.map((g) => ({
			...g,
			bids: [...g.bids].sort((a, b) => b.priceSats - a.priceSats),
			totalSats: g.bids.reduce((s, b) => s + b.priceSats, 0)
		}))
		.sort((a, b) => b.totalSats - a.totalSats)
}
